import React, { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Charts for the IC3 figures, drawn as inline SVG.
 *
 * Colours track the IC3 report's own palette, deepened so each holds against a
 * white surface and re-stepped where the original pair was indistinguishable
 * under colour-vision deficiency. Every value is directly labelled or reachable
 * from the data table, so nothing depends on colour or hover alone.
 */

const INK = '#1A1A1A';
const MUTED = '#5A5A5A';
const GRID = '#DEDEDE';
const AXIS = '#B4B4B4';

/** IC3's line/primary blue. */
export const IC3_BLUE = '#0A6EA8';

/**
 * IC3's crime-type hues, snapped to steps that pass the palette checks.
 * Slot 3 is purple where the report used a neutral grey: a chroma-free slot
 * can't be told apart by hue, and IC3's orange/green pair sat at ΔE 4.6 for
 * deuteranopes — below the level any amount of labelling can rescue.
 */
export const IC3_CATEGORICAL = ['#0A6EA8', '#B58200', '#7A5AA6', '#E8853A', '#2F6B2F'];

/* ------------------------------------------------------------------ *
 * Shared chrome
 * ------------------------------------------------------------------ */

const Tooltip = ({ x, y, lines }) => (
  <div
    className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md bg-black/85 px-3 py-2 text-center text-[12px] leading-tight text-white shadow-lg"
    style={{ left: `${x}%`, top: `${y}%` }}
  >
    {lines.map((line, i) => (
      <div key={line} className={i === 0 ? 'font-semibold' : 'opacity-90'}>
        {line}
      </div>
    ))}
  </div>
);

const DataTable = ({ columns, rows }) => (
  <div className="mt-3 overflow-x-auto">
    <table className="w-full border-collapse text-left text-[13px]">
      <thead>
        <tr className="border-b border-black/20">
          {columns.map((col) => (
            <th key={col} className="py-1.5 pr-4 font-semibold text-black">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[0]} className="border-b border-black/10">
            {row.map((cell, i) => (
              <td key={i} className="py-1.5 pr-4 text-black/80">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ChartCard = ({ title, note, tableColumns, tableRows, children }) => {
  const [showTable, setShowTable] = useState(false);

  return (
    <figure className="flex flex-col rounded-lg border border-black/10 bg-white p-3 md:p-4">
      <figcaption className="mb-2 text-center text-[14px] font-bold text-black md:text-[15px]">
        {title}
      </figcaption>

      <div className="relative">{children}</div>

      <div className="mt-2 flex items-center justify-between gap-3">
        {note ? <p className="text-[12px] text-black/55">{note}</p> : <span />}
        <button
          type="button"
          onClick={() => setShowTable((open) => !open)}
          aria-expanded={showTable}
          className="flex-shrink-0 text-[12px] text-[#0A6EA8] underline underline-offset-2 hover:text-[#07547f]"
        >
          {showTable ? 'Hide data' : 'Show data'}
        </button>
      </div>

      {showTable && <DataTable columns={tableColumns} rows={tableRows} />}
    </figure>
  );
};

/** Snap a raw tick interval up to 1 / 2 / 2.5 / 5 / 10 in its magnitude. */
const niceStep = (value) => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const scaled = value / magnitude;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10;
  return step * magnitude;
};

/**
 * Ticks that clear the data without stranding it in the lower half — aim for
 * five intervals, then round the top up to the next whole tick.
 */
const buildTicks = (max) => {
  const step = niceStep(max / 5);
  const axisMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let value = 0; value <= axisMax + step / 2; value += step) {
    ticks.push(Number(value.toFixed(6)));
  }
  return { axisMax, ticks };
};

/* ------------------------------------------------------------------ *
 * Trend line
 * ------------------------------------------------------------------ */

export const TrendChart = ({ title, points, note, format, axisFormat, color = IC3_BLUE }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const svgRef = useRef(null);

  const W = 820;
  const H = 300;
  const padLeft = 68;
  const padRight = 28;
  const padTop = 22;
  const padBottom = 38;

  const plotW = W - padLeft - padRight;
  const plotH = H - padTop - padBottom;

  const { axisMax, ticks } = useMemo(
    () => buildTicks(Math.max(...points.map((p) => p.value))),
    [points]
  );

  const x = useCallback(
    (i) => padLeft + (plotW * i) / (points.length - 1),
    [plotW, points.length]
  );
  const y = useCallback((value) => padTop + plotH - (value / axisMax) * plotH, [axisMax, plotH]);

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.value)}`).join(' ');
  const areaPath = `${linePath} L ${x(points.length - 1)} ${padTop + plotH} L ${padLeft} ${
    padTop + plotH
  } Z`;

  const gradientId = `area-${title.replace(/\W/g, '')}`;

  // Map a pointer position onto the nearest data point.
  const handleMove = (event) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const localX = ((event.clientX - rect.left) / rect.width) * W;
    const ratio = (localX - padLeft) / plotW;
    const index = Math.round(ratio * (points.length - 1));
    setActiveIndex(Math.min(points.length - 1, Math.max(0, index)));
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    setActiveIndex((current) => {
      const start = current === null ? 0 : current;
      const next = event.key === 'ArrowRight' ? start + 1 : start - 1;
      return Math.min(points.length - 1, Math.max(0, next));
    });
  };

  const active = activeIndex === null ? null : points[activeIndex];

  return (
    <ChartCard
      title={title}
      note={note}
      tableColumns={['Year', 'Value']}
      tableRows={points.map((p) => [p.label, p.display])}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full touch-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A6EA8]"
        role="img"
        tabIndex={0}
        aria-label={`${title}. ${points.map((p) => `${p.label}: ${p.display}`).join('. ')}`}
        onMouseMove={handleMove}
        onMouseLeave={() => setActiveIndex(null)}
        onKeyDown={handleKeyDown}
        onBlur={() => setActiveIndex(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.20" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Horizontal grid + value axis */}
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={padLeft}
              x2={W - padRight}
              y1={y(tick)}
              y2={y(tick)}
              stroke={GRID}
              strokeWidth="1"
            />
            <text x={padLeft - 12} y={y(tick) + 4} textAnchor="end" fontSize="12" fill={MUTED}>
              {axisFormat(tick)}
            </text>
          </g>
        ))}

        <line
          x1={padLeft}
          x2={W - padRight}
          y1={padTop + plotH}
          y2={padTop + plotH}
          stroke={AXIS}
          strokeWidth="1"
        />

        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Crosshair for the hovered point */}
        {active && (
          <line
            x1={x(activeIndex)}
            x2={x(activeIndex)}
            y1={padTop}
            y2={padTop + plotH}
            stroke={color}
            strokeWidth="1"
            strokeDasharray="4 3"
          />
        )}

        {points.map((p, i) => {
          const isActive = i === activeIndex;
          const isEnd = i === 0 || i === points.length - 1;
          return (
            <g key={p.label}>
              <circle
                cx={x(i)}
                cy={y(p.value)}
                r={isActive ? 7 : 4.5}
                fill={color}
                stroke="#FFFFFF"
                strokeWidth="2"
              />
              {/* Endpoints stay labelled; the rest are read off the axis or hover */}
              {isEnd && !isActive && (
                <text
                  x={x(i)}
                  y={y(p.value) - 14}
                  textAnchor={i === 0 ? 'start' : 'end'}
                  fontSize="13"
                  fontWeight="600"
                  fill={INK}
                >
                  {p.display}
                </text>
              )}
              <text x={x(i)} y={H - 16} textAnchor="middle" fontSize="12.5" fill={MUTED}>
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>

      {active && (
        <Tooltip
          x={(x(activeIndex) / W) * 100}
          y={((y(active.value) - 14) / H) * 100}
          lines={[active.display, active.label]}
        />
      )}
    </ChartCard>
  );
};

/* ------------------------------------------------------------------ *
 * Two stacked panels sharing one x-axis
 * ------------------------------------------------------------------ */

/**
 * Count above, share below, one x-axis between them — the shape the Census
 * uses. Two measures on different scales get their own panel and their own
 * y-axis rather than being forced onto a single pair of axes, which is what a
 * dual-axis chart does and why it misleads.
 */
export const DualPanelChart = ({
  title,
  points,
  barLabel,
  lineLabel,
  barFormat,
  lineFormat,
  barColor = IC3_BLUE,
  lineColor = '#E8853A',
  note,
  labelEvery = 1,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const svgRef = useRef(null);

  const W = 820;
  const H = 352;
  const padLeft = 58;
  const padRight = 22;

  const barTop = 26;
  const barH = 136;
  const lineTop = 232;
  const lineH = 82;

  const plotW = W - padLeft - padRight;

  const bars = useMemo(() => buildTicks(Math.max(...points.map((p) => p.bar))), [points]);
  const lines = useMemo(() => buildTicks(Math.max(...points.map((p) => p.line))), [points]);

  const x = (i) => padLeft + (plotW * (i + 0.5)) / points.length;
  const yBar = (v) => barTop + barH - (v / bars.axisMax) * barH;
  const yLine = (v) => lineTop + lineH - (v / lines.axisMax) * lineH;
  const barW = Math.min(46, (plotW / points.length) * 0.62);

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${yLine(p.line)}`)
    .join(' ');

  const handleMove = (event) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const localX = ((event.clientX - rect.left) / rect.width) * W;
    const index = Math.floor(((localX - padLeft) / plotW) * points.length);
    setActiveIndex(Math.min(points.length - 1, Math.max(0, index)));
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    setActiveIndex((current) => {
      const start = current === null ? 0 : current;
      const next = event.key === 'ArrowRight' ? start + 1 : start - 1;
      return Math.min(points.length - 1, Math.max(0, next));
    });
  };

  const active = activeIndex === null ? null : points[activeIndex];

  const axisRow = (top, height, ticks, axisMax, format) =>
    ticks.map((tick) => (
      <g key={tick}>
        <line
          x1={padLeft}
          x2={W - padRight}
          y1={top + height - (tick / axisMax) * height}
          y2={top + height - (tick / axisMax) * height}
          stroke={GRID}
          strokeWidth="1"
        />
        <text
          x={padLeft - 10}
          y={top + height - (tick / axisMax) * height + 5}
          textAnchor="end"
          fontSize="16"
          fill={MUTED}
        >
          {format(tick)}
        </text>
      </g>
    ));

  return (
    <ChartCard
      title={title}
      note={note}
      tableColumns={['Year', barLabel, lineLabel]}
      tableRows={points.map((p) => [p.label, barFormat(p.bar), lineFormat(p.line)])}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full touch-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A6EA8]"
        role="img"
        tabIndex={0}
        aria-label={`${title}. ${points
          .map((p) => `${p.label}: ${barFormat(p.bar)}, ${lineFormat(p.line)}`)
          .join('. ')}`}
        onMouseMove={handleMove}
        onMouseLeave={() => setActiveIndex(null)}
        onKeyDown={handleKeyDown}
        onBlur={() => setActiveIndex(null)}
      >
        {/* Upper panel — magnitude. Panel labels sit left-aligned above their
            own axis so a long one can't run off the edge. */}
        <text x={4} y={18} textAnchor="start" fontSize="18" fill={INK}>
          {barLabel}
        </text>
        {axisRow(barTop, barH, bars.ticks, bars.axisMax, barFormat)}
        {points.map((p, i) => (
          <rect
            key={p.label}
            x={x(i) - barW / 2}
            y={yBar(p.bar)}
            width={barW}
            height={barTop + barH - yBar(p.bar)}
            fill={barColor}
            opacity={activeIndex !== null && activeIndex !== i ? 0.45 : 1}
          />
        ))}
        <line
          x1={padLeft}
          x2={W - padRight}
          y1={barTop + barH}
          y2={barTop + barH}
          stroke={AXIS}
          strokeWidth="1"
        />
        {points.map((p, i) =>
          i % labelEvery === 0 ? (
            <text
              key={`bx-${p.label}`}
              x={x(i)}
              y={barTop + barH + 20}
              textAnchor="middle"
              fontSize="17"
              fill={MUTED}
            >
              {p.label}
            </text>
          ) : null
        )}

        {/* Lower panel — share */}
        <text x={4} y={lineTop - 14} textAnchor="start" fontSize="18" fill={INK}>
          {lineLabel}
        </text>
        {axisRow(lineTop, lineH, lines.ticks, lines.axisMax, lineFormat)}
        <path
          d={linePath}
          fill="none"
          stroke={lineColor}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {points.map((p, i) => (
          <circle
            key={`lp-${p.label}`}
            cx={x(i)}
            cy={yLine(p.line)}
            r={activeIndex === i ? 6 : 3.5}
            fill={lineColor}
            stroke="#FFFFFF"
            strokeWidth="2"
          />
        ))}
        <line
          x1={padLeft}
          x2={W - padRight}
          y1={lineTop + lineH}
          y2={lineTop + lineH}
          stroke={AXIS}
          strokeWidth="1"
        />
        {points.map((p, i) =>
          i % labelEvery === 0 ? (
            <text
              key={`lx-${p.label}`}
              x={x(i)}
              y={lineTop + lineH + 20}
              textAnchor="middle"
              fontSize="17"
              fill={MUTED}
            >
              {p.label}
            </text>
          ) : null
        )}

        {/* One crosshair spanning both panels, so the year reads across */}
        {active && (
          <line
            x1={x(activeIndex)}
            x2={x(activeIndex)}
            y1={barTop}
            y2={lineTop + lineH}
            stroke="#1A1A1A"
            strokeOpacity="0.35"
            strokeWidth="1"
            strokeDasharray="4 3"
          />
        )}
      </svg>

      {active && (
        <Tooltip
          x={(x(activeIndex) / W) * 100}
          y={((yBar(active.bar) - 10) / H) * 100}
          lines={[active.label, barFormat(active.bar), lineFormat(active.line)]}
        />
      )}
    </ChartCard>
  );
};

/* ------------------------------------------------------------------ *
 * Grouped vertical columns — two series side by side
 * ------------------------------------------------------------------ */

/**
 * Two-series column chart. The pair is gold/blue rather than two steps of one
 * blue: same-hue steps either read as grey or land under the normal-vision
 * separation floor at the lightness these bars need.
 */
export const GroupedColumnChart = ({ title, groups, series, note, unitSuffix = '' }) => {
  const [active, setActive] = useState(null);

  const W = 820;
  const H = 292;
  const padLeft = 30;
  const padRight = 20;
  const padTop = 30;
  const padBottom = 44;

  const plotW = W - padLeft - padRight;
  const plotH = H - padTop - padBottom;

  const peak = Math.max(...groups.flatMap((g) => series.map((s) => g.values[s.key])));
  const { axisMax, ticks } = useMemo(() => buildTicks(peak), [peak]);

  const bandW = plotW / groups.length;
  const barW = Math.min(56, (bandW * 0.62) / series.length);
  const y = (value) => padTop + plotH - (value / axisMax) * plotH;

  return (
    <ChartCard
      title={title}
      note={note}
      tableColumns={['Group', ...series.map((s) => s.label)]}
      tableRows={groups.map((g) => [
        g.label,
        ...series.map((s) => `${g.values[s.key]}${unitSuffix}`),
      ])}
    >
      {/* Legend — always present for two or more series */}
      <div className="mb-2 flex items-center justify-center gap-5">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-2 text-[13px] text-black">
            <span
              aria-hidden="true"
              className="inline-block h-3 w-3 rounded-[2px]"
              style={{ backgroundColor: s.color }}
            />
            {s.label}
          </span>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={`${title}. ${groups
          .map((g) => `${g.label}: ${series.map((s) => `${s.label} ${g.values[s.key]}`).join(', ')}`)
          .join('. ')}`}
      >
        {ticks.map((tick) => (
          <line
            key={tick}
            x1={padLeft}
            x2={W - padRight}
            y1={y(tick)}
            y2={y(tick)}
            stroke={GRID}
            strokeWidth="1"
          />
        ))}

        {groups.map((group, gi) => {
          const bandX = padLeft + gi * bandW;
          const groupW = barW * series.length + 6 * (series.length - 1);
          const startX = bandX + (bandW - groupW) / 2;
          return (
            <g
              key={group.label}
              onMouseEnter={() => setActive(gi)}
              onMouseLeave={() => setActive(null)}
            >
              <rect x={bandX} y={padTop} width={bandW} height={plotH} fill="transparent" />
              {series.map((s, si) => {
                const value = group.values[s.key];
                const barX = startX + si * (barW + 6);
                const barY = y(value);
                return (
                  <g key={s.key}>
                    <rect
                      x={barX}
                      y={barY}
                      width={barW}
                      height={padTop + plotH - barY}
                      fill={s.color}
                      opacity={active !== null && active !== gi ? 0.45 : 1}
                    />
                    <text
                      x={barX + barW / 2}
                      y={barY - 7}
                      textAnchor="middle"
                      fontSize="17"
                      fontWeight={active === gi ? '700' : '400'}
                      fill={INK}
                    >
                      {value}
                    </text>
                  </g>
                );
              })}
              <text
                x={bandX + bandW / 2}
                y={H - 20}
                textAnchor="middle"
                fontSize="15"
                fill={MUTED}
              >
                {group.label}
              </text>
            </g>
          );
        })}

        <line
          x1={padLeft}
          x2={W - padRight}
          y1={padTop + plotH}
          y2={padTop + plotH}
          stroke={AXIS}
          strokeWidth="1"
        />
      </svg>
    </ChartCard>
  );
};

/* ------------------------------------------------------------------ *
 * Horizontal bars
 * ------------------------------------------------------------------ */

// Bar with only its far end rounded, so the mark stays anchored to the baseline.
const barPath = (x, y, w, h, r) => {
  const radius = Math.min(r, w);
  return [
    `M ${x} ${y}`,
    `H ${x + w - radius}`,
    `Q ${x + w} ${y} ${x + w} ${y + radius}`,
    `V ${y + h - radius}`,
    `Q ${x + w} ${y + h} ${x + w - radius} ${y + h}`,
    `H ${x}`,
    'Z',
  ].join(' ');
};

export const BarChart = ({
  title,
  bars,
  note,
  color = IC3_BLUE,
  colors,
  labelPlacement = 'left',
  labelWidth = 96,
  valueHeading = 'Value',
  textured = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const svgRef = useRef(null);

  const W = 820;
  const rowH = 25;
  const gap = 7;
  const padTop = 4;
  const padBottom = 4;
  const H = padTop + bars.length * (rowH + gap) - gap + padBottom;

  /*
   * Hover is resolved against bands that tile the whole plot rather than the
   * bars themselves: the gutters between bars belong to their nearest bar, so
   * crossing one never drops the highlight and flickers.
   */
  const bandFor = (localY) => {
    const pitch = rowH + gap;
    const index = Math.floor((localY - padTop + gap / 2) / pitch);
    return Math.min(bars.length - 1, Math.max(0, index));
  };

  const handleMove = (event) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const localY = ((event.clientY - rect.top) / rect.height) * H;
    setActiveIndex(bandFor(localY));
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    setActiveIndex((current) => {
      const start = current === null ? 0 : current;
      const next = event.key === 'ArrowDown' ? start + 1 : start - 1;
      return Math.min(bars.length - 1, Math.max(0, next));
    });
  };

  const left = labelPlacement === 'left' ? labelWidth + 14 : 6;
  const valueGutter = labelPlacement === 'left' ? 86 : 320;
  const trackW = W - left - valueGutter;
  const max = Math.max(...bars.map((b) => b.value));
  const hueFor = (i) => (colors ? colors[i % colors.length] : color);

  return (
    <ChartCard
      title={title}
      note={note}
      tableColumns={['Category', valueHeading]}
      tableRows={bars.map((b) => [b.label, b.display])}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full touch-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A6EA8]"
        role="img"
        tabIndex={0}
        aria-label={`${title}. ${bars.map((b) => `${b.label}: ${b.display}`).join('. ')}`}
        onMouseMove={handleMove}
        onMouseLeave={() => setActiveIndex(null)}
        onKeyDown={handleKeyDown}
        onBlur={() => setActiveIndex(null)}
      >
        <defs>
          {/* Echoes the striped fill IC3 used, and doubles as CVD relief */}
          <pattern id="hatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="2.5" />
          </pattern>
        </defs>

        {labelPlacement === 'left' && (
          <line x1={left - 9} x2={left - 9} y1={padTop} y2={H - padBottom} stroke={AXIS} strokeWidth="2" />
        )}

        {bars.map((bar, i) => {
          const barY = padTop + i * (rowH + gap);
          const w = Math.max(5, (bar.value / max) * trackW);
          const dimmed = activeIndex !== null && activeIndex !== i;
          return (
            <g key={bar.label}>
              {labelPlacement === 'left' && (
                <text x={left - 20} y={barY + rowH / 2 + 5} textAnchor="end" fontSize="14" fill={INK}>
                  {bar.label}
                </text>
              )}

              <path
                d={barPath(left, barY, w, rowH, 4)}
                fill={hueFor(i)}
                opacity={dimmed ? 0.45 : 1}
              />
              {textured && (
                <path
                  d={barPath(left, barY, w, rowH, 4)}
                  fill="url(#hatch)"
                  opacity={dimmed ? 0.45 : 1}
                />
              )}

              <text
                x={left + w + 12}
                y={barY + rowH / 2 + 5}
                fontSize="14"
                fontWeight={activeIndex === i ? '700' : '400'}
                fill={INK}
              >
                {labelPlacement === 'left' ? bar.display : `${bar.label}; ${bar.display}`}
              </text>
            </g>
          );
        })}
      </svg>
    </ChartCard>
  );
};
